import React from 'react';
import './PageHeader.css';

/**
 * Shared page header (ST-00097).
 *
 * Replaces the hand-copied "icon + title | controls" bar whose single
 * non-wrapping flex row let the controls paint over the page title on phones.
 * The header reflows from its own width (see PageHeader.css):
 * narrow → title + actions on one row, toolbar on the rows below;
 * wide   → icon, title/subtitle, toolbar and actions on one row.
 * The bar bleeds across the content pane's horizontal padding (--page-pad-x,
 * set by .page-pane) so it runs flush to the sidebar on every page (ST-00181).
 *
 * @param {React.ComponentType} icon       react-icons component for the brand tile (wide only)
 * @param {string}  title
 * @param {React.ReactNode} subtitle        shown from ~560px of header width
 * @param {React.ReactNode} actions         buttons that stay beside the title at every width — keep them compact
 * @param {React.ReactNode} toolbar         view switches / filters; each direct child is one wrapping unit
 * @param {boolean} inline                 false = always stacked (toolbar too long for one row)
 * @param {'start'|'end'} toolbarAlign      wide layout: 'start' sits after the title behind a divider, 'end' pushes right
 * @param {boolean} sticky                  pin to the top of the scroll area (from sm up)
 * @param {string}  className               extra classes on the outer bar (e.g. margin-bottom); the bar already bleeds across the pane padding (--page-pad-x)
 */
const PageHeader = ({
  icon: Icon,
  title,
  subtitle,
  actions,
  toolbar,
  toolbarAlign = 'end',
  inline = true,
  sticky = true,
  className = '',
}) => {
  const modifiers = [
    sticky ? 'page-header--sticky sticky top-0 z-10' : '',
    toolbar ? `page-header--toolbar-${toolbarAlign}` : 'page-header--no-toolbar',
    inline ? '' : 'page-header--stacked',
  ].join(' ');

  return (
    <header
      className={`page-header ${modifiers} bg-surface border-b border-slate-200 ${className}`}
    >
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-3">
        <div className="page-header__row">
          <div className="page-header__title">
            {Icon && (
              <div
                className="page-header__icon items-center justify-center w-11 h-11 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-sm flex-shrink-0"
                aria-hidden="true"
              >
                <Icon className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="page-header__heading text-lg font-bold text-slate-800 tracking-tight leading-tight">
                {title}
              </h1>
              {subtitle && (
                <p className="page-header__subtitle text-slate-500 text-sm mt-0.5">{subtitle}</p>
              )}
            </div>
          </div>

          {toolbar && <div className="page-header__toolbar">{toolbar}</div>}

          {actions && <div className="page-header__actions">{actions}</div>}
        </div>
      </div>
    </header>
  );
};

export default PageHeader;
